import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { QRStatusBadge } from "@/components/QRStatusBadge";
import { toast } from "sonner";
import { Search, Eye, Printer, PackagePlus, Tag, Ban, Plus, Trash2 } from "lucide-react";
import { printQRLabels } from "@/lib/printHelper";

const TITLES = {
  BLANK: "Blank QR Codes",
  PRODUCT_ASSIGNED: "Assigned QR Codes",
};

export default function QRCodes({ status }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [products, setProducts] = useState([]);
  const [viewQR, setViewQR] = useState(null);
  const [assignQR, setAssignQR] = useState(null);
  const [packageQR, setPackageQR] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/store/qr", { params: { status, search: search || undefined } })
      .then(r => { setRows(r.data); setSelected(new Set()); })
      .catch(e => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get("/store/products").then(r => setProducts(r.data)).catch(() => {});
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [status]);
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const toggleSelect = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === rows.length) setSelected(new Set());
    else setSelected(new Set(rows.map(r => r.qr_code_id)));
  };

  const selectedRows = useMemo(
    () => rows.filter(r => selected.has(r.qr_code_id)),
    [rows, selected]
  );

  const onVoid = async (qr) => {
    if (!confirm(`Void QR ${qr.qr_code_id}? This cannot be undone.`)) return;
    try {
      await api.post(`/store/qr/${qr.qr_code_id}/void`);
      toast.success("QR voided.");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid={`qr-codes-${status}`} className="space-y-6">
      <div>
        <div className="text-xs font-mono-tech uppercase tracking-[0.2em] text-cyan-400 mb-2">/ QR Codes</div>
        <h1 className="font-display text-3xl font-bold tracking-tight">{TITLES[status]}</h1>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input
            data-testid="qr-search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by QR ID or product…"
            className="pl-10 bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white focus-visible:ring-cyan-400"
          />
        </div>
        <div className="text-xs font-mono-tech text-slate-500">
          {loading ? "Loading…" : `${rows.length} QR · ${selected.size} selected`}
        </div>
        <div className="flex gap-2 ml-auto">
          <Button
            data-testid="print-selected-btn"
            variant="outline"
            disabled={selectedRows.length === 0}
            onClick={() => printQRLabels(selectedRows)}
            className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
          >
            <Printer className="w-4 h-4 mr-2" /> Print Selected ({selectedRows.length})
          </Button>
          <Button
            data-testid="print-all-btn"
            variant="outline"
            disabled={rows.length === 0}
            onClick={() => printQRLabels(rows)}
            className="bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
          >
            <Printer className="w-4 h-4 mr-2" /> Print All
          </Button>
        </div>
      </div>

      {rows.length === 0 && !loading ? (
        <div className="border border-dashed border-slate-200 dark:border-slate-800 rounded-lg py-20 text-center bg-slate-100/30 dark:bg-slate-900/30">
          <div className="font-display text-lg text-slate-700 dark:text-slate-300 mb-1">No QR codes here</div>
          <div className="text-sm text-slate-500">Head to the QR Code Generator to create new ones.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <div className="flex items-center justify-between col-span-full text-xs px-1">
            <label className="inline-flex items-center gap-2 text-slate-600 dark:text-slate-400 cursor-pointer">
              <input
                data-testid="select-all-checkbox"
                type="checkbox"
                checked={selected.size === rows.length && rows.length > 0}
                onChange={toggleAll}
                className="accent-cyan-400"
              />
              Select all
            </label>
          </div>
          {rows.map((q) => (
            <div
              key={q.qr_code_id}
              data-testid={`qr-card-${q.qr_code_id}`}
              className={[
                "border rounded-lg bg-slate-100/40 dark:bg-slate-900/40 p-4 flex flex-col transition-colors",
                selected.has(q.qr_code_id) ? "border-cyan-500/40 ring-1 ring-cyan-500/20" : "border-slate-200 dark:border-slate-800 hover:border-slate-300 hover:dark:border-slate-700",
              ].join(" ")}
            >
              <div className="flex items-start gap-3">
                <label className="cursor-pointer pt-1">
                  <input
                    data-testid={`qr-select-${q.qr_code_id}`}
                    type="checkbox"
                    checked={selected.has(q.qr_code_id)}
                    onChange={() => toggleSelect(q.qr_code_id)}
                    className="accent-cyan-400"
                  />
                </label>
                <div className="bg-white p-1.5 rounded">
                  <img src={q.qr_image} alt={q.qr_code_id} className="w-16 h-16" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-mono-tech text-xs text-cyan-300 truncate">{q.qr_code_id}</div>
                  <div className="text-xs text-slate-600 dark:text-slate-400 truncate mt-0.5">{q.store_name}</div>
                  <div className="mt-1.5"><QRStatusBadge status={q.status} /></div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-slate-200/60 dark:border-slate-800/60 text-xs text-slate-600 dark:text-slate-400 flex items-center gap-2">
                {q.assigned_product_photo ? (
                  <img src={q.assigned_product_photo} alt="" className="w-6 h-6 rounded object-cover border border-slate-200 dark:border-slate-800" />
                ) : null}
                <span className="truncate">{q.assigned_product_name || <span className="text-slate-500">No product assigned</span>}</span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-1.5">
                <Button
                  data-testid={`view-qr-${q.qr_code_id}`}
                  size="sm" variant="outline"
                  onClick={() => setViewQR(q)}
                  className="h-8 text-xs bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-200 hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
                >
                  <Eye className="w-3 h-3 mr-1" /> View
                </Button>
                <Button
                  data-testid={`print-qr-${q.qr_code_id}`}
                  size="sm" variant="outline"
                  onClick={() => printQRLabels([q])}
                  className="h-8 text-xs bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-200 hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
                >
                  <Printer className="w-3 h-3 mr-1" /> Print
                </Button>
                <Button
                  data-testid={`assign-qr-${q.qr_code_id}`}
                  size="sm" variant="outline"
                  onClick={() => setAssignQR(q)}
                  className="h-8 text-xs bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-blue-300 hover:bg-blue-500/10 hover:text-blue-200"
                >
                  <Tag className="w-3 h-3 mr-1" /> Assign
                </Button>
                <Button
                  data-testid={`add-pkg-qr-${q.qr_code_id}`}
                  size="sm" variant="outline"
                  onClick={() => setPackageQR(q)}
                  className="h-8 text-xs bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-cyan-300 hover:bg-cyan-500/10 hover:text-cyan-200"
                >
                  <PackagePlus className="w-3 h-3 mr-1" /> Add Pkg
                </Button>
                <Button
                  data-testid={`void-qr-${q.qr_code_id}`}
                  size="sm" variant="ghost"
                  onClick={() => onVoid(q)}
                  className="col-span-2 h-8 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  <Ban className="w-3 h-3 mr-1" /> Void QR
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View QR dialog */}
      <Dialog open={!!viewQR} onOpenChange={(o) => !o && setViewQR(null)}>
        <DialogContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">QR Code</DialogTitle>
            <DialogDescription className="text-slate-600 dark:text-slate-400 font-mono-tech text-xs">
              {viewQR?.qr_code_id}
            </DialogDescription>
          </DialogHeader>
          {viewQR && (
            <div className="text-center">
              <div className="bg-white p-4 inline-block rounded-md">
                <img src={viewQR.qr_image} alt={viewQR.qr_code_id} className="w-56 h-56" />
              </div>
              <div className="mt-3 font-mono-tech text-xs text-slate-600 dark:text-slate-400 break-all">{viewQR.qr_code_data}</div>
              <div className="mt-2 text-sm">
                <div className="text-slate-700 dark:text-slate-300">Store: <b>{viewQR.store_name}</b></div>
                <div className="text-slate-600 dark:text-slate-400">Product: {viewQR.assigned_product_name || "—"}</div>
                <div className="mt-2"><QRStatusBadge status={viewQR.status} /></div>
              </div>
              <Button
                data-testid="dialog-print-qr"
                onClick={() => printQRLabels([viewQR])}
                className="mt-4 bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium w-full"
              >
                <Printer className="w-4 h-4 mr-2" /> Print Label
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AssignProductDialog
        qr={assignQR}
        products={products}
        onClose={() => setAssignQR(null)}
        onSaved={() => { setAssignQR(null); load(); }}
      />

      <AddPackageInfoDialog
        qr={packageQR}
        products={products}
        onClose={() => setPackageQR(null)}
        onSaved={() => { setPackageQR(null); load(); }}
      />
    </div>
  );
}

function AssignProductDialog({ qr, products, onClose, onSaved }) {
  const [productId, setProductId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (qr) setProductId(qr.assigned_product_id || "");
  }, [qr]);

  const onSave = async () => {
    if (!productId) {
      toast.error("Select a product.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/store/qr/${qr.qr_code_id}/assign-product`, { product_id: productId });
      toast.success("Product assigned.");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!qr} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">Assign Product</DialogTitle>
          <DialogDescription className="text-slate-600 dark:text-slate-400 font-mono-tech text-xs">
            {qr?.qr_code_id}
          </DialogDescription>
        </DialogHeader>
        {qr && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2 text-xs">
              <Label className="col-span-1 text-slate-500 self-center">QR Code ID</Label>
              <div className="col-span-2 font-mono-tech text-cyan-300">{qr.qr_code_id}</div>
              <Label className="col-span-1 text-slate-500 self-center">Store</Label>
              <div className="col-span-2 text-slate-900 dark:text-white">{qr.store_name}</div>
            </div>
            <div>
              <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-600 dark:text-slate-400">Product</Label>
              <Select value={productId} onValueChange={setProductId}>
                <SelectTrigger data-testid="assign-product-select" className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white mt-1.5">
                  <SelectValue placeholder="Choose a product…" />
                </SelectTrigger>
                <SelectContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white">
                  {products.map(p => (
                    <SelectItem key={p.id} value={p.id} data-testid={`assign-product-opt-${p.id}`}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button
              data-testid="assign-product-save-btn"
              onClick={onSave}
              disabled={saving}
              className="w-full bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium"
            >
              {saving ? "Saving…" : "Save Assignment"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AddPackageInfoDialog({ qr, products, onClose, onSaved }) {
  const [form, setForm] = useState({
    package_name: "", description: "", receiver_name: "",
    receiver_phone: "", receiver_address: "", special_instructions: "",
  });
  const [items, setItems] = useState([{ _uid: crypto.randomUUID(), item_name: "", quantity: 1, description: "", product_id: "" }]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!qr) return;
    setForm({
      package_name: qr.assigned_product_name ? `${qr.assigned_product_name} package` : "",
      description: "",
      receiver_name: "", receiver_phone: "", receiver_address: "", special_instructions: "",
    });
    if (qr.assigned_product_id) {
      setItems([{
        _uid: crypto.randomUUID(),
        item_name: qr.assigned_product_name,
        quantity: 1, description: "",
        product_id: qr.assigned_product_id,
      }]);
    } else {
      setItems([{ _uid: crypto.randomUUID(), item_name: "", quantity: 1, description: "", product_id: "" }]);
    }
  }, [qr]);

  const updateItem = (idx, patch) => {
    setItems(items.map((it, i) => i === idx ? { ...it, ...patch } : it));
  };
  const addItem = () => setItems([...items, { _uid: crypto.randomUUID(), item_name: "", quantity: 1, description: "", product_id: "" }]);
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const onProductPick = (idx, productId) => {
    const p = products.find(x => x.id === productId);
    updateItem(idx, { product_id: productId, item_name: p ? p.name : "" });
  };

  const onSave = async () => {
    if (!form.package_name || !form.receiver_name || !form.receiver_phone || !form.receiver_address) {
      toast.error("Fill in package name, receiver name, phone, and address.");
      return;
    }
    const cleanItems = items
      .filter(it => it.item_name?.trim())
      .map(it => ({ ...it, product_id: it.product_id || null, quantity: Number(it.quantity) || 1 }));
    if (cleanItems.length === 0) {
      toast.error("Add at least one item.");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/store/qr/${qr.qr_code_id}/create-package`, { ...form, items: cleanItems });
      toast.success("Package created and ready for pickup.");
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!qr} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin">
        <DialogHeader>
          <DialogTitle className="font-display">Add Package Information</DialogTitle>
          <DialogDescription className="text-slate-600 dark:text-slate-400 font-mono-tech text-xs">
            {qr?.qr_code_id} · Store: {qr?.store_name}
          </DialogDescription>
        </DialogHeader>
        {qr && (
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Package Name">
                <Input
                  data-testid="pkginfo-name"
                  value={form.package_name}
                  onChange={e => setForm({ ...form, package_name: e.target.value })}
                  className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800"
                />
              </Field>
              <Field label="Receiver Name">
                <Input
                  data-testid="pkginfo-receiver-name"
                  value={form.receiver_name}
                  onChange={e => setForm({ ...form, receiver_name: e.target.value })}
                  className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800"
                />
              </Field>
              <Field label="Receiver Phone">
                <Input
                  data-testid="pkginfo-receiver-phone"
                  value={form.receiver_phone}
                  onChange={e => setForm({ ...form, receiver_phone: e.target.value })}
                  className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800"
                />
              </Field>
              <Field label="Description">
                <Input
                  data-testid="pkginfo-desc"
                  value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800"
                />
              </Field>
              <div className="col-span-2">
                <Field label="Receiver Address">
                  <Textarea
                    data-testid="pkginfo-receiver-address"
                    value={form.receiver_address}
                    onChange={e => setForm({ ...form, receiver_address: e.target.value })}
                    className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white"
                    rows={2}
                  />
                </Field>
              </div>
              <div className="col-span-2">
                <Field label="Special Instructions (optional)">
                  <Textarea
                    data-testid="pkginfo-instructions"
                    value={form.special_instructions}
                    onChange={e => setForm({ ...form, special_instructions: e.target.value })}
                    className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white"
                    rows={2}
                  />
                </Field>
              </div>
            </div>

            <div className="border-t border-slate-200 dark:border-slate-800 pt-4">
              <div className="flex justify-between items-center mb-2">
                <Label className="text-xs font-mono-tech uppercase tracking-wider text-slate-600 dark:text-slate-400">Items</Label>
                <Button
                  data-testid="pkginfo-add-item"
                  size="sm" variant="outline" onClick={addItem}
                  className="h-7 text-xs bg-slate-100 dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white hover:bg-slate-200 hover:dark:bg-slate-800 hover:text-slate-900 hover:dark:text-white"
                >
                  <Plus className="w-3 h-3 mr-1" /> Add Item
                </Button>
              </div>
              <div className="space-y-2">
                {items.map((it, idx) => (
                  <div key={it._uid || idx} className="grid grid-cols-12 gap-2 items-end border border-slate-200 dark:border-slate-800 rounded-md p-3 bg-slate-100/40 dark:bg-slate-900/40">
                    <div className="col-span-12 sm:col-span-5">
                      <Label className="text-[11px] text-slate-500 mb-1 block">Product (optional)</Label>
                      <Select value={it.product_id || ""} onValueChange={(v) => onProductPick(idx, v)}>
                        <SelectTrigger data-testid={`pkginfo-item-product-${idx}`} className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white">
                          <SelectValue placeholder="Type item name or pick…" />
                        </SelectTrigger>
                        <SelectContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white">
                          {products.map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-7 sm:col-span-4">
                      <Label className="text-[11px] text-slate-500 mb-1 block">Item Name</Label>
                      <Input
                        data-testid={`pkginfo-item-name-${idx}`}
                        value={it.item_name}
                        onChange={e => updateItem(idx, { item_name: e.target.value })}
                        className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800"
                      />
                    </div>
                    <div className="col-span-3 sm:col-span-2">
                      <Label className="text-[11px] text-slate-500 mb-1 block">Qty</Label>
                      <Input
                        data-testid={`pkginfo-item-qty-${idx}`}
                        type="number" min={1}
                        value={it.quantity}
                        onChange={e => updateItem(idx, { quantity: e.target.value })}
                        className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800"
                      />
                    </div>
                    <div className="col-span-2 sm:col-span-1 flex justify-end">
                      {items.length > 1 && (
                        <button
                          data-testid={`pkginfo-remove-item-${idx}`}
                          onClick={() => removeItem(idx)}
                          className="text-red-400 hover:text-red-300 h-9 w-9 grid place-items-center rounded-md hover:bg-red-500/10"
                          type="button"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <Button
              data-testid="pkginfo-save-btn"
              onClick={onSave}
              disabled={saving}
              className="w-full bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium"
            >
              {saving ? "Saving…" : "Create Package"}
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] font-mono-tech uppercase tracking-wider text-slate-500">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
